// ChatBubbleNotification — Persistent floating chat button + notification toast
// KHLIFI HOUCEM / FORMATEUR CLOUD && DEVSECOPS
import { useEffect, useRef, useState } from "react";
import useChatStore from "@/stores/chatStore";
import { useAuth } from "@/lib/auth-context";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { MessageCircle, X } from "lucide-react";

const getInitials = (f, l) =>
  `${(f || "")[0] || ""}${(l || "")[0] || ""}`.toUpperCase();

export default function ChatBubbleNotification() {
  const {
    notification, clearNotification, openChat, setActiveConversation,
    totalUnread, chatOpen
  } = useChatStore();
  const { user } = useAuth();

  // Check if notepad button exists on the page (admin/trainer on workspace pages)
  const [hasNotepad, setHasNotepad] = useState(false);
  useEffect(() => {
    const check = () => {
      const notepadBtn = document.querySelector('.floating-notepad-trigger');
      setHasNotepad(!!notepadBtn);
    };
    check();
    // Re-check after a short delay (for dynamic rendering)
    const t = setTimeout(check, 500);
    return () => clearTimeout(t);
  });

  // Toast for the latest incoming message (auto-dismiss after 8s but button stays)
  const [toast, setToast] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!notification) return;
    setToast(notification);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setToast(null);
    }, 8000);
    return () => clearTimeout(timerRef.current);
  }, [notification]);

  // Don't show the floating button if chat dialog is already open
  if (chatOpen) return null;

  const handleToastClick = () => {
    if (!toast) return;
    const { sender, conversationId } = toast;
    setToast(null);
    clearNotification();
    openChat(sender, conversationId);
    setActiveConversation(conversationId);
  };

  const handleDismissToast = (e) => {
    e.stopPropagation();
    setToast(null);
    clearNotification();
  };

  const handleFabClick = () => {
    if (toast) {
      handleToastClick();
      return;
    }
    openChat(null, null);
  };

  const roleColors = {
    admin: "#8b5cf6",
    trainer: "#3b82f6",
    candidate: "#22c55e",
    manager: "#f59e0b",
  };

  // Position: shift left when notepad is present (admin/trainer on workspace pages)
  const fabRight = hasNotepad ? 88 : 24;

  return (
    <div style={{
      position: "fixed",
      bottom: 24,
      right: fabRight,
      zIndex: 60,
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-end",
      gap: 12,
      transition: "right 0.3s ease",
    }}>

      {/* Toast notification popup */}
      {toast && (
        <div
          onClick={handleToastClick}
          style={{
            animation: "chatBubbleSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards",
            cursor: "pointer",
            maxWidth: 340,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 12,
              padding: 14,
              borderRadius: 16,
              background: "var(--background)",
              border: `1px solid ${(roleColors[toast.sender?.role] || "#8b5cf6")}40`,
              boxShadow: `0 8px 32px ${(roleColors[toast.sender?.role] || "#8b5cf6")}20, 0 4px 16px rgba(0,0,0,0.2)`,
            }}
          >
            <div style={{ position: "relative", flexShrink: 0 }}>
              <Avatar className="h-10 w-10 ring-2" style={{ "--tw-ring-color": (roleColors[toast.sender?.role] || "#8b5cf6") + "60" }}>
                <AvatarImage src={toast.sender?.avatar} />
                <AvatarFallback
                  className="text-xs font-bold text-white"
                  style={{ background: roleColors[toast.sender?.role] || "#8b5cf6" }}
                >
                  {getInitials(toast.sender?.first_name, toast.sender?.last_name)}
                </AvatarFallback>
              </Avatar>
              <span style={{
                position: "absolute", top: -4, right: -4,
                width: 16, height: 16, borderRadius: "50%",
                background: roleColors[toast.sender?.role] || "#8b5cf6",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <MessageCircle style={{ width: 10, height: 10, color: "white" }} />
              </span>
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <p style={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", margin: 0 }}>
                  {toast.sender?.first_name} {toast.sender?.last_name}
                </p>
                <button
                  onClick={handleDismissToast}
                  style={{ flexShrink: 0, padding: 2, borderRadius: 4, background: "none", border: "none", cursor: "pointer", color: "var(--muted-foreground)" }}
                >
                  <X style={{ width: 14, height: 14 }} />
                </button>
              </div>
              <p style={{ fontSize: 11, color: "var(--muted-foreground)", textTransform: "capitalize", margin: "1px 0 4px" }}>
                {toast.sender?.role === "trainer" ? "Formateur" : toast.sender?.role === "admin" ? "Admin" : toast.sender?.role}
              </p>
              <p style={{ fontSize: 12, color: "var(--foreground)", opacity: 0.8, margin: 0, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.4 }}>
                {toast.message?.content}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Persistent Floating Action Button */}
      <button
        onClick={handleFabClick}
        style={{
          position: "relative",
          width: 48,
          height: 48,
          borderRadius: "50%",
          background: "linear-gradient(135deg, #8b5cf6, #6d28d9)",
          color: "white",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 20px rgba(139, 92, 246, 0.4), 0 2px 8px rgba(0,0,0,0.15)",
          transition: "transform 0.2s, box-shadow 0.2s",
        }}
        onMouseEnter={e => {
          e.currentTarget.style.transform = "scale(1.1)";
          e.currentTarget.style.boxShadow = "0 6px 28px rgba(139, 92, 246, 0.5), 0 4px 12px rgba(0,0,0,0.2)";
        }}
        onMouseLeave={e => {
          e.currentTarget.style.transform = "scale(1)";
          e.currentTarget.style.boxShadow = "0 4px 20px rgba(139, 92, 246, 0.4), 0 2px 8px rgba(0,0,0,0.15)";
        }}
        title="Messages"
      >
        <MessageCircle style={{ width: 22, height: 22 }} />

        {/* Unread badge */}
        {totalUnread > 0 && (
          <span
            style={{
              position: "absolute",
              top: -4,
              right: -4,
              minWidth: 20,
              height: 20,
              borderRadius: 10,
              background: "#ef4444",
              color: "white",
              fontSize: 10,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 5px",
              border: "2px solid var(--background)",
              boxShadow: "0 2px 8px rgba(239, 68, 68, 0.4)",
              animation: "badgePulse 2s ease-in-out infinite",
            }}
          >
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        )}
      </button>

      <style>{`
        @keyframes chatBubbleSlideIn {
          0% { opacity: 0; transform: translateY(20px) scale(0.95); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes badgePulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
      `}</style>
    </div>
  );
}
